import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  defaultLocale,
  locales,
  localeMeta,
  translations,
  type InterpolationValues,
  type Locale,
  type TranslationKey,
} from './translations';

const LOCALE_STORAGE_KEY = 'walletgate_admin_locale';

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, values?: InterpolationValues) => string;
}

function detectInitialLocale(): Locale {
  if (typeof window === 'undefined') return defaultLocale;

  const saved = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  if (saved && locales.includes(saved as Locale)) {
    return saved as Locale;
  }

  const browser = (window.navigator.language || '').toLowerCase();
  const match = locales.find((candidate) => browser.startsWith(candidate));
  return match || defaultLocale;
}

function interpolate(template: string, values?: InterpolationValues): string {
  if (!values) return template;

  return template.replace(/\{(\w+)\}/g, (_, key: string) => {
    if (values[key] === undefined) return `{${key}}`;
    return String(values[key]);
  });
}

const fallbackContext: I18nContextValue = {
  locale: defaultLocale,
  setLocale: () => undefined,
  t: (key, values) => interpolate(translations[defaultLocale][key], values),
};

const I18nContext = createContext<I18nContextValue>(fallbackContext);

interface I18nProviderProps {
  children: ReactNode;
}

export function I18nProvider({ children }: I18nProviderProps): JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(detectInitialLocale);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
  }, []);

  const t = useCallback(
    (key: TranslationKey, values?: InterpolationValues): string => {
      const phrase = translations[locale][key] || translations[defaultLocale][key];
      return interpolate(phrase, values);
    },
    [locale],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ locale, setLocale, t }),
    [locale, setLocale, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}

export { localeMeta, locales };
